import type { EventBroadcast } from '@sunucupark/client-core';
import type { RealtimeEvent } from '@sunucupark/shared';
import { contractSchema } from '@/shared/contracts';

/** The event contract loads with the first message, not with the page (ADR 0006). */
const eventSchema = contractSchema((contracts) => contracts.RealtimeEventSchema);

/**
 * Passes what the leader tab saw to the other tabs of the same browser, so a
 * single event stream serves every window (ADR 0008). Messages are validated:
 * a channel is shared by whatever runs on this origin.
 */
export class RealtimeBroadcast implements EventBroadcast<RealtimeEvent> {
  private readonly channel: BroadcastChannel | null;
  private readonly listeners = new Set<(event: RealtimeEvent) => void>();

  constructor(name = 'postamerkezi:events') {
    this.channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(name);
    if (this.channel) {
      this.channel.onmessage = (message: MessageEvent<unknown>) => {
        void eventSchema().then((schema) => {
          const parsed = schema.safeParse(message.data);
          if (!parsed.success) return;
          for (const listener of this.listeners) listener(parsed.data);
        });
      };
    }
  }

  publish(event: RealtimeEvent): void {
    this.channel?.postMessage(event);
  }

  subscribe(listener: (event: RealtimeEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  close(): void {
    this.channel?.close();
  }
}
